import React,{useEffect,useMemo,useState}from"react";
import{Box,Paper,Typography,Button,TextField,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TablePagination,useMediaQuery,FormControl,InputLabel,Select,MenuItem}from"@mui/material";
import{useNavigate}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";
import{useAuth}from"../../context/AuthContext";

function formatMoney(value){
  const number=Number(value||0);
  return number.toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function formatStock(value){
  const number=Number(value||0);
  return number.toLocaleString("es-EC",{minimumFractionDigits:0,maximumFractionDigits:6});
}

function formatPercent(value){
  if(Number(value)===999)return"No objeto";
  return`${Number(value||0)}%`;
}

function getIvaValue(product){
  if(product.iva_valor!==undefined&&product.iva_valor!==null)return Number(product.iva_valor||0);
  if(Number(product.iva_porcentaje)===999)return 0;
  return Number(product.pvp1||0)*(Number(product.iva_porcentaje||0)/100);
}

function getFinalPrice(product){
  return Number(product.pvp1||0)+getIvaValue(product);
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

export default function AdminProducts(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError}=useAlert();
  const{user}=useAuth();
  const navigate=useNavigate();
  const isMobile=useMediaQuery("(max-width:900px)");
  const[products,setProducts]=useState([]);
  const[search,setSearch]=useState("");
  const[stockFilter,setStockFilter]=useState("todos");
  const[statusFilter,setStatusFilter]=useState("todos");
  const[ecommerceFilter,setEcommerceFilter]=useState("todos");
  const[loading,setLoading]=useState(false);
  const[page,setPage]=useState(0);
  const rowsPerPage=isMobile?10:30;
  const token=localStorage.getItem("accessToken");
  const canView=userCan(user,"products.view");
  const canCreate=userCan(user,"products.create");
  const canEdit=userCan(user,"products.edit");
  const canDelete=userCan(user,"products.delete");
  const showActions=canEdit||canDelete;
  const canManageCatalog=canCreate||canEdit||canDelete;
  const tableColumns=showActions?8:7;

  const loadProducts=async()=>{
    if(!canView)return;
    try{
      setLoading(true);
      const params=new URLSearchParams();
      if(search.trim())params.append("q",search.trim());
      if(stockFilter!=="todos")params.append("control_stock",stockFilter);
      if(statusFilter!=="todos")params.append("estado",statusFilter);
      if(ecommerceFilter!=="todos")params.append("ecommerce",ecommerceFilter);
      const response=await fetch(`/api/products/products/?${params.toString()}`,{headers:{Authorization:`Bearer ${token}`}});
      const data=await response.json();
      if(!response.ok){
        showError(data.error||data.detail||"No se pudieron cargar los productos");
        return;
      }
      setProducts(data.results||[]);
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>{
      setPage(0);
      loadProducts();
    },350);
    return()=>clearTimeout(delay);
  },[search,stockFilter,statusFilter,ecommerceFilter,canView]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const visibleProducts=useMemo(()=>{
    const start=page*rowsPerPage;
    return products.slice(start,start+rowsPerPage);
  },[products,page,rowsPerPage]);

  const handleChangePage=(_event,newPage)=>{
    setPage(newPage);
  };

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{borderColor:dashboardTheme.button_color,bgcolor:`${dashboardTheme.button_color}12`,boxShadow:"none"},
  };

  const smallButtonSx={
    minHeight:32,
    px:1.5,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{borderColor:dashboardTheme.button_color,bgcolor:`${dashboardTheme.button_color}12`,boxShadow:"none"},
  };

  const selectSx={
    minWidth:{xs:"100%",sm:150},
    ...dashboardTheme.inputStyles,
    "& .MuiSelect-icon":{color:dashboardTheme.text_color},
  };

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:0.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none",
        "& .MuiMenu-list":{p:0.5},
        "& .MuiMenuItem-root":{
          fontSize:14,
          borderRadius:"7px",
          color:dashboardTheme.text_color,
          "&:hover":{bgcolor:dashboardTheme.dashboard_background},
          "&.Mui-selected":{bgcolor:dashboardTheme.dashboard_background},
          "&.Mui-selected:hover":{bgcolor:dashboardTheme.dashboard_background},
        },
      },
    },
  };

  const secondaryText=dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#64748b";

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
              <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Productos</Typography>
              {(canManageCatalog||canCreate)&&(
                <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},gap:1}}>
                  {canManageCatalog&&<Button variant="outlined" onClick={()=>navigate("/admin-panel/productos/categorias")} sx={outlinedButtonSx}>Categorías y marcas</Button>}
                  {canCreate&&<Button variant="contained" onClick={()=>navigate("/admin-panel/productos/nuevo")} sx={dashboardTheme.buttonStyles.contained}>Añadir producto</Button>}
                </Box>
              )}
            </Box>
            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={{...dashboardTheme.tableStyles.toolbar,display:"flex",flexWrap:"wrap",alignItems:"center",gap:1}}>
                <TextField fullWidth size="small" label="Buscar por nombre, código de barras, código auxiliar o categoría" value={search} onChange={e=>setSearch(e.target.value)} sx={{maxWidth:{xs:"100%",md:430},...dashboardTheme.inputStyles}}/>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Inventario</InputLabel>
                  <Select label="Inventario" value={stockFilter} onChange={e=>setStockFilter(e.target.value)} MenuProps={selectMenuProps}>
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="true">Stock</MenuItem>
                    <MenuItem value="false">No aplica</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Estado</InputLabel>
                  <Select label="Estado" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} MenuProps={selectMenuProps}>
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="activo">Activos</MenuItem>
                    <MenuItem value="inactivo">Inactivos</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Ecommerce</InputLabel>
                  <Select label="Ecommerce" value={ecommerceFilter} onChange={e=>setEcommerceFilter(e.target.value)} MenuProps={selectMenuProps}>
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="true">Publicado</MenuItem>
                    <MenuItem value="false">No publicado</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:240}}>Producto</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:150}}>Categoría</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:150}}>Inventario</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>PVP</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>IVA</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Precio final</TableCell>
                        <TableCell sx={dashboardTheme.tableStyles.headCell}>Estado</TableCell>
                        {showActions&&<TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Acciones</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleProducts.map(product=>{
                        const productId=product.id||product.pk;
                        const lowStock=product.control_stock&&Number(product.stock||0)<=Number(product.stock_minimo||0);
                        return(
                          <TableRow key={productId} hover sx={{...dashboardTheme.tableStyles.bodyRow,cursor:canEdit?"pointer":"default"}} onDoubleClick={()=>{
                            if(canEdit&&productId)navigate(`/admin-panel/productos/${productId}`);
                          }}>
                            <TableCell>
                              <Typography sx={{fontSize:14,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.25}}>{product.name||"-"}</Typography>
                              {product.control_stock&&product.barcode&&<Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,lineHeight:1.35,mt:0.25}}>Cód. Barras: {product.barcode}</Typography>}
                              {product.control_stock&&product.codigo_auxiliar&&<Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,lineHeight:1.35}}>Aux: {product.codigo_auxiliar}</Typography>}
                            </TableCell>
                            <TableCell>
                              <Typography sx={{fontSize:13.5,fontWeight:500,color:dashboardTheme.text_color,lineHeight:1.3}}>{product.category?.name||"Sin categoría"}</Typography>
                            </TableCell>
                            <TableCell>
                              {product.control_stock?(
                                <>
                                  <Typography sx={{fontSize:13.5,fontWeight:600,color:lowStock?"#D32F2F":dashboardTheme.text_color,lineHeight:1.3}}>Stock: {formatStock(product.stock)}</Typography>
                                  <Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,lineHeight:1.3,mt:0.25}}>Mínimo: {formatStock(product.stock_minimo)}</Typography>
                                </>
                              ):(
                                <Typography sx={{fontSize:13,fontWeight:500,color:secondaryText}}>No aplica</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              <Typography sx={{fontSize:13.5,fontWeight:600,color:dashboardTheme.text_color}}>${formatMoney(product.pvp1)}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              <Typography sx={{fontSize:13,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.25}}>{formatPercent(product.iva_porcentaje)}</Typography>
                              <Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,lineHeight:1.25}}>${formatMoney(getIvaValue(product))}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.primary_color,lineHeight:1.25}}>${formatMoney(getFinalPrice(product))}</Typography>
                            </TableCell>
                            <TableCell sx={{whiteSpace:"nowrap"}}>
                              <Typography sx={{fontSize:13.5,fontWeight:600,color:product.estado==="activo"?dashboardTheme.text_color:"#D32F2F"}}>{product.estado==="activo"?"Activo":"Inactivo"}</Typography>
                              <Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,lineHeight:1.3,mt:0.25}}>{product.ecommerce?"Ecommerce":"No ecommerce"}</Typography>
                            </TableCell>
                            {showActions&&(
                              <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                                {canEdit&&<Button variant="outlined" size="small" onClick={event=>{
                                  event.stopPropagation();
                                  if(productId)navigate(`/admin-panel/productos/${productId}`);
                                  else showError("No se pudo abrir el producto porque no tiene ID.");
                                }} sx={smallButtonSx}>Editar</Button>}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                      {!loading&&visibleProducts.length===0&&canView&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No hay productos que coincidan con los filtros</TableCell>
                        </TableRow>
                      )}
                      {loading&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>Cargando productos...</TableCell>
                        </TableRow>
                      )}
                      {!canView&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>No tienes permisos para ver productos</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              <TablePagination component="div" count={products.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} rowsPerPageOptions={[]} labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`} sx={dashboardTheme.tableStyles.pagination}/>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}